import { useRef } from "react";
import { Controller, type Control, type UseFormRegister } from "react-hook-form";
import { RichTextEditor, type RichTextEditorHandle } from "@legal-500/legal500-design-system";
import { EntitySearchPicker } from "@/components/ResearchCms";
import { SectionVisibilityIcon } from "../SectionVisibilityIcon";
import type { EntitySearchResult } from "@/components/ResearchCms/EntitySearchPicker/EntitySearchPicker";
import { buildFirmSpan, buildLawyerSpan } from "../../utils/entityReference";
import type { OrgFormValues } from "../../schemas/orgViewSchemas";
import shared from "../cmsShared.module.css";
import styles from "./EditorialTab.module.css";

type EditorialTabProps = {
  control: Control<OrgFormValues>;
  register: UseFormRegister<OrgFormValues>;
  showClerksCommentary: boolean;
};

export function EditorialTab({ control, register, showClerksCommentary }: EditorialTabProps) {
  const editorRef = useRef<RichTextEditorHandle>(null);

  function handleEntitySelect(entity: EntitySearchResult, type: "firm" | "lawyer") {
    const html = type === "firm" ? buildFirmSpan(entity) : buildLawyerSpan(entity);
    editorRef.current?.insertContent(`${html} `);
  }

  return (
    <>
      <div className={shared.sectionCard} style={{ position: "relative" }}>
        <SectionVisibilityIcon publishable tooltip="This content is shown on legal500.com" />
        <h2 className={shared.sectionHeading}>Editorial Content</h2>
        <div className={styles.entityPickers}>
          <EntitySearchPicker
            type="firm"
            label="+ Insert Firm Name"
            onSelect={entity => handleEntitySelect(entity, "firm")}
          />
          <EntitySearchPicker
            type="lawyer"
            label="+ Insert Lawyer Name"
            onSelect={entity => handleEntitySelect(entity, "lawyer")}
          />
        </div>
        <Controller
          control={control}
          name="postContent"
          render={({ field }) => (
            <RichTextEditor
              ref={editorRef}
              value={field.value}
              onChange={field.onChange}
              preset="full"
              entityReferences
              htmlTab
              minHeight={200}
              label=""
            />
          )}
        />
      </div>

      {showClerksCommentary && (
        <div className={shared.sectionCard} style={{ position: "relative" }}>
          <SectionVisibilityIcon publishable tooltip="This content is shown on legal500.com" />
          <h2 className={shared.sectionHeading}>Clerks' Commentary</h2>
          <textarea
            {...register("clerksCommentary")}
            className={shared.textarea}
            rows={5}
            placeholder="Additional editorial content about the clerking team"
          />
        </div>
      )}

      <div className={shared.sectionCard} style={{ position: "relative" }}>
        <SectionVisibilityIcon publishable={false} />
        <h2 className={shared.sectionHeading}>Researcher Notes</h2>
        <textarea
          {...register("researcherNotes")}
          className={shared.textarea}
          rows={4}
          placeholder="Internal working notes (never published)"
        />
      </div>

      <div className={shared.sectionCard} style={{ position: "relative" }}>
        <SectionVisibilityIcon publishable={false} />
        <h2 className={shared.sectionHeading}>Editor Comment Box</h2>
        <textarea
          {...register("editorCommentBox")}
          className={shared.textarea}
          rows={4}
          placeholder="Editor notes for the researcher (internal)"
        />
      </div>
    </>
  );
}
